import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Copy, Check, Link2Off, UserPlus, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export function ShareModal({ open, onClose, session, onShareChange }) {
  const [copied, setCopied] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviting, setInviting] = useState(false);
  const shareUrl = session?.share_token ? `${window.location.origin}/share/${session.share_token}` : "";

  const handleEnable = async () => {
    const { data } = await api.post(`/sessions/${session.id}/share`);
    onShareChange({ ...session, share_token: data.share_token });
  };

  const handleRevoke = async () => {
    await api.delete(`/sessions/${session.id}/share`);
    onShareChange({ ...session, share_token: null });
    toast.success("Share link revoked");
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const { data } = await api.post(`/sessions/${session.id}/invite`, { email: inviteEmail.trim() });
      onShareChange(data);
      setInviteEmail("");
      toast.success("Editor invited");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not invite editor");
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveEditor = async (email) => {
    const { data } = await api.delete(`/sessions/${session.id}/invite`, { params: { email } });
    onShareChange(data);
    toast.success("Editor removed");
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="share-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Share this session</DialogTitle>
          <DialogDescription>Generate a view-only link, or invite prayer leaders to co-edit.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {session?.share_token ? (
            <>
              <p className="text-sm text-muted-foreground">Anyone with this link can view your session (read-only).</p>
              <div className="flex gap-2">
                <Input readOnly value={shareUrl} data-testid="share-link-input" />
                <Button size="icon" variant="outline" onClick={handleCopy} data-testid="share-copy-button">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
              <Button variant="ghost" className="text-[var(--brand-crimson)] w-full" onClick={handleRevoke} data-testid="share-revoke-button">
                <Link2Off className="h-4 w-4 mr-2" /> Revoke link
              </Button>
            </>
          ) : (
            <>
              <p className="text-sm text-muted-foreground">Generate a view-only link to collaborate with other prayer leaders.</p>
              <Button className="w-full bg-[var(--brand-crimson)] hover:bg-[var(--brand-crimson-hover)] text-white" onClick={handleEnable} data-testid="share-generate-button">
                Generate share link
              </Button>
            </>
          )}
        </div>

        <div className="pt-4 border-t border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] space-y-3">
          <p className="text-sm font-medium">Invite editors</p>
          <p className="text-xs text-muted-foreground">Invited leaders can edit segments, reorder, and export &mdash; but cannot delete this session.</p>
          <div className="flex gap-2">
            <Input
              type="email"
              placeholder="leader@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleInvite()}
              data-testid="invite-email-input"
            />
            <Button variant="outline" onClick={handleInvite} disabled={inviting} data-testid="invite-email-button">
              <UserPlus className="h-4 w-4" />
            </Button>
          </div>
          {session?.editor_emails?.length > 0 && (
            <div className="space-y-1.5" data-testid="editor-emails-list">
              {session.editor_emails.map((email) => (
                <div
                  key={email}
                  className="flex items-center justify-between bg-[var(--brand-bg)] dark:bg-[var(--brand-bg-dark)] rounded-md px-3 py-1.5 text-sm"
                  data-testid={`editor-row-${email}`}
                >
                  <span>{email}</span>
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => handleRemoveEditor(email)} data-testid={`editor-remove-${email}`}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
